/**
 * ASR语音识别工具类 - 语音识别核心管理模块
 * 支持Chrome浏览器
 * 使用POST API方式进行实时语音处理
 */

import { asrStart, asrAudio, asrFinish } from '@/api/sysManage/aiCustomPage'
import store from '@/store'

// 音频处理器类型枚举
const ProcessorType = {
  AUDIO_WORKLET: 'audioworklet',
  SCRIPT_PROCESSOR: 'scriptprocessor',
  MEDIA_RECORDER: 'mediarecorder'
}

// ASR来源枚举
const Source = {
  CARGO_WARE: 'cargo-ware',
  CARGO_WARE_X: 'cargo-ware-x',
  WO_BAO: 'wo-bao'
}

// 浏览器兼容性检测工具
const BrowserCapability = {
  checkMediaDevices() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
  },

  checkMediaRecorder() {
    return !!window.MediaRecorder
  },

  checkAudioContext() {
    return !!(window.AudioContext || window.webkitAudioContext)
  },

  checkAudioWorklet() {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext
      const testContext = new AudioContextClass()
      const hasWorklet = !!(testContext.audioWorklet && testContext.audioWorklet.addModule)
      testContext.close()
      return hasWorklet
    } catch (error) {
      return false
    }
  },

  getSupportedMimeTypes() {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm;codecs=pcm',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus'
    ]

    return types.filter(type => MediaRecorder.isTypeSupported(type))
  },

  isChrome() {
    return navigator.userAgent.includes('Chrome') && !navigator.userAgent.includes('Edge')
  },

  isFirefox() {
    return navigator.userAgent.includes('Firefox')
  },

  // 检测最佳音频处理器类型
  getBestProcessorType() {
    if (this.checkAudioWorklet()) {
      return ProcessorType.AUDIO_WORKLET
    }
    if (this.checkAudioContext()) {
      return ProcessorType.SCRIPT_PROCESSOR
    }
    if (this.checkMediaRecorder()) {
      return ProcessorType.MEDIA_RECORDER
    }
    return null
  }
}

// 错误处理器
const ErrorHandler = {
  PERMISSION_DENIED: '用户拒绝了麦克风权限',
  NOT_SUPPORTED: '当前浏览器不支持语音识别',
  NETWORK_ERROR: '网络连接错误，请检查网络',
  API_ERROR: '语音识别服务异常',
  AUDIO_ERROR: '音频处理错误',
  SESSION_ERROR: '会话创建失败',

  handle(error) {
    const message = this[error.code] || error.message || '未知错误'
    return {
      code: error.code,
      message,
      userMessage: message
    }
  }
}

// 基础音频处理器抽象类 - 定义统一接口
class BaseAudioProcessor {
  constructor() {
    this.targetSampleRate = 16000
    this.targetChannels = 1
    this.segmentDurationMs = 200
    this.onDataAvailable = null
    this.isRecording = false
  }

  // 抽象方法 - 子类必须实现
  async startRecording() {
    throw new Error('startRecording method must be implemented by subclass')
  }

  stopRecording() {
    throw new Error('stopRecording method must be implemented by subclass')
  }

  isSupported() {
    throw new Error('isSupported method must be implemented by subclass')
  }

  cleanup() {
    this.onDataAvailable = null
    this.isRecording = false
  }

  // 通用音频处理方法 - 确保输出16kHz单声道200ms数据
  async processAudioChunk(float32Data, sourceSampleRate = 44100) {
    try {
      console.log(`[音频处理] 开始处理音频块: ${float32Data.length} samples, 源采样率: ${sourceSampleRate}Hz`)

      // 1. 音频质量检查
      const audioStats = this.analyzeAudioQuality(float32Data)
      console.log(`[音频处理] 音频质量: RMS=${audioStats.rms.toFixed(4)}, Peak=${audioStats.peak.toFixed(4)}, 静音比例=${(audioStats.silenceRatio * 100).toFixed(1)}%`)

      // 2. 重采样到16kHz (如果需要)
      let resampledData = float32Data
      if (sourceSampleRate !== this.targetSampleRate) {
        resampledData = this.resample(float32Data, sourceSampleRate, this.targetSampleRate)
        console.log(`[音频处理] 重采样完成: ${float32Data.length} → ${resampledData.length} samples`)
      }

      // 3. 音频增强（针对Firefox低质量音频）
      if (BrowserCapability.isFirefox() && audioStats.rms < 0.01) {
        resampledData = this.enhanceAudioForFirefox(resampledData)
        console.log(`[音频处理] Firefox音频增强完成`)
      }

      // 4. 转换Float32Array为PCM16格式
      const pcmData = this.floatToPCM16(resampledData)
      console.log(`[音频处理] PCM16转换完成: ${pcmData.length} samples (${pcmData.length * 2} bytes)`)

      // 5. GZIP压缩
      const compressedData = await this.compressData(pcmData)
      console.log(`[音频处理] 压缩完成: ${compressedData.length} bytes`)

      // 6. Base64编码 (格式: "H4sIAAAAAAAA...")
      const base64Data = this.arrayBufferToBase64(compressedData)
      console.log(`[音频处理] Base64编码完成: ${base64Data.length} chars, 开头: ${base64Data.substring(0, 20)}...`)

      return base64Data
    } catch (error) {
      console.error('[音频处理] 处理失败:', error)
      throw { code: 'AUDIO_ERROR', message: error.message }
    }
  }

  // 分析音频质量
  analyzeAudioQuality(float32Data) {
    let sum = 0
    let peak = 0
    let silenceCount = 0
    const silenceThreshold = 0.001

    for (let i = 0; i < float32Data.length; i++) {
      const abs = Math.abs(float32Data[i])
      sum += abs * abs
      peak = Math.max(peak, abs)
      if (abs < silenceThreshold) {
        silenceCount++
      }
    }

    return {
      rms: Math.sqrt(sum / float32Data.length),
      peak: peak,
      silenceRatio: silenceCount / float32Data.length
    }
  }

  // Firefox音频增强
  enhanceAudioForFirefox(float32Data) {
    const enhanced = new Float32Array(float32Data.length)
    const gain = 2.0 // 适度增益

    for (let i = 0; i < float32Data.length; i++) {
      // 应用增益并限制在[-1, 1]范围内
      enhanced[i] = Math.max(-1, Math.min(1, float32Data[i] * gain))
    }

    return enhanced
  }

  // 改进的重采样算法 - 针对Firefox优化
  resample(inputData, inputSampleRate, outputSampleRate) {
    if (inputSampleRate === outputSampleRate) {
      return inputData
    }

    console.log(`[音频处理] 重采样: ${inputSampleRate}Hz → ${outputSampleRate}Hz, 输入长度: ${inputData.length}`)

    const ratio = inputSampleRate / outputSampleRate
    const outputLength = Math.round(inputData.length / ratio)
    const output = new Float32Array(outputLength)

    // 使用更好的抗混叠重采样
    if (ratio > 1) {
      // 下采样：需要低通滤波防止混叠
      for (let i = 0; i < outputLength; i++) {
        const centerIndex = i * ratio
        const startIndex = Math.max(0, Math.floor(centerIndex - ratio / 2))
        const endIndex = Math.min(inputData.length - 1, Math.ceil(centerIndex + ratio / 2))

        let sum = 0
        let count = 0
        for (let j = startIndex; j <= endIndex; j++) {
          sum += inputData[j]
          count++
        }
        output[i] = count > 0 ? sum / count : 0
      }
    } else {
      // 上采样：使用三次插值
      for (let i = 0; i < outputLength; i++) {
        const index = i * ratio
        const indexFloor = Math.floor(index)
        const indexCeil = Math.min(indexFloor + 1, inputData.length - 1)
        const t = index - indexFloor

        // 线性插值（保持简单高效）
        output[i] = inputData[indexFloor] * (1 - t) + inputData[indexCeil] * t
      }
    }

    console.log(`[音频处理] 重采样完成: 输出长度: ${output.length}`)
    return output
  }

  // Float32转PCM16
  floatToPCM16(floatArray) {
    const pcm16 = new Int16Array(floatArray.length)
    for (let i = 0; i < floatArray.length; i++) {
      const sample = Math.max(-1, Math.min(1, floatArray[i]))
      pcm16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF
    }
    return pcm16
  }

  // GZIP压缩PCM数据
  async compressData(pcmData) {
    try {
      // 将PCM16数据转换为Uint8Array字节流
      const uint8Array = new Uint8Array(pcmData.buffer)

      // Firefox的CompressionStream可能有兼容性问题，优先检查
      if (typeof CompressionStream !== 'undefined') {
        try {
          const compressed = await this.gzipCompress(uint8Array)

          // 验证压缩结果的有效性
          if (compressed && compressed.length > 0 && compressed.length < uint8Array.length) {
            console.log(`[音频处理] GZIP压缩成功: ${uint8Array.length} → ${compressed.length} bytes`)
            return compressed
          } else {
            console.warn('[音频处理] GZIP压缩结果异常，使用原始数据')
            return uint8Array
          }
        } catch (compressionError) {
          console.warn('[音频处理] GZIP压缩失败，使用原始数据:', compressionError)
          return uint8Array
        }
      } else {
        // 浏览器不支持CompressionStream，返回原始PCM数据
        console.warn('[音频处理] 浏览器不支持GZIP压缩，发送原始PCM数据')
        return uint8Array
      }
    } catch (error) {
      console.error('[音频处理] 压缩处理失败:', error)
      // 压缩失败时返回原始PCM数据
      return new Uint8Array(pcmData.buffer)
    }
  }

  // 使用CompressionStream进行GZIP压缩（Firefox兼容性优化）
  async gzipCompress(data) {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('GZIP压缩超时'))
      }, 5000) // 5秒超时

      try {
        const stream = new CompressionStream('gzip')
        const writer = stream.writable.getWriter()
        const reader = stream.readable.getReader()

        // 异步写入数据
        const writePromise = writer.write(data).then(() => writer.close())

        // 异步读取压缩结果
        const chunks = []
        const readPromise = (async() => {
          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            chunks.push(value)
          }
        })()

        // 等待写入和读取完成
        Promise.all([writePromise, readPromise]).then(() => {
          clearTimeout(timeout)

          // 合并所有压缩数据块
          if (chunks.length === 0) {
            reject(new Error('GZIP压缩无输出'))
            return
          }

          const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
          const compressed = new Uint8Array(totalLength)
          let offset = 0

          for (const chunk of chunks) {
            compressed.set(chunk, offset)
            offset += chunk.length
          }

          console.log(`[音频处理] GZIP压缩: ${data.length} bytes → ${compressed.length} bytes (${Math.round(compressed.length / data.length * 100)}%)`)
          resolve(compressed)
        }).catch(error => {
          clearTimeout(timeout)
          reject(error)
        })
      } catch (error) {
        clearTimeout(timeout)
        reject(error)
      }
    })
  }

  // ArrayBuffer转Base64
  arrayBufferToBase64(buffer) {
    let binary = ''
    const bytes = new Uint8Array(buffer)
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary)
  }
}

// AudioWorklet音频处理器 - 现代浏览器优先选择（Firefox 76+, Chrome 66+）
class AudioWorkletProcessor extends BaseAudioProcessor {
  constructor() {
    super()
    this.audioContext = null
    this.stream = null
    this.source = null
    this.workletNode = null
    this.audioBuffer = []
    this.bufferTargetSize = Math.round(this.targetSampleRate * this.segmentDurationMs / 1000) // 200ms = 3200 samples at 16kHz
  }

  isSupported() {
    return BrowserCapability.checkMediaDevices() && BrowserCapability.checkAudioWorklet()
  }

  async startRecording() {
    if (!this.isSupported()) {
      throw { code: 'NOT_SUPPORTED' }
    }

    try {
      // Firefox兼容性处理：构建灵活的音频约束
      const audioConstraints = {
        channelCount: this.targetChannels,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: false
      }

      // Chrome可以指定采样率，Firefox让其自动选择
      if (BrowserCapability.isChrome()) {
        audioConstraints.sampleRate = this.targetSampleRate
      }

      const constraints = {
        audio: audioConstraints
      }

      // 获取麦克风流
      this.stream = await navigator.mediaDevices.getUserMedia(constraints)

      // 创建AudioContext
      const AudioContextClass = window.AudioContext || window.webkitAudioContext

      // Firefox兼容性处理：不强制指定采样率，让其使用系统默认
      const audioContextOptions = { latencyHint: 'interactive' }

      // 只有在Chrome中才尝试指定采样率
      if (BrowserCapability.isChrome()) {
        audioContextOptions.sampleRate = this.targetSampleRate
      }

      try {
        this.audioContext = new AudioContextClass(audioContextOptions)
        console.log(`[AudioWorklet] 创建AudioContext，采样率: ${this.audioContext.sampleRate}Hz`)
      } catch (error) {
        // 如果指定采样率失败，使用默认设置
        console.warn(`[AudioWorklet] 指定采样率失败，使用默认设置:`, error)
        this.audioContext = new AudioContextClass({ latencyHint: 'interactive' })
        console.log(`[AudioWorklet] 降级AudioContext，采样率: ${this.audioContext.sampleRate}Hz`)
      }

      // 创建worklet模块（内联方式）
      const workletCode = `
        class ASRProcessor extends AudioWorkletProcessor {
          constructor() {
            super()
            this.buffer = []
            this.targetBufferSize = Math.round(sampleRate * 0.2) // 200ms
            console.log('[AudioWorklet] Processor initialized, sampleRate:', sampleRate, 'targetBufferSize:', this.targetBufferSize)
          }
          
          process(inputs, outputs) {
            const input = inputs[0]
            if (input && input.length > 0) {
              const channelData = input[0]
              if (channelData && channelData.length > 0) {
                // 累积音频数据到缓冲区
                for (let i = 0; i < channelData.length; i++) {
                  this.buffer.push(channelData[i])
                }
                
                // 当缓冲区达到200ms时，发送数据
                while (this.buffer.length >= this.targetBufferSize) {
                  const chunk = this.buffer.splice(0, this.targetBufferSize)
                  const audioData = new Float32Array(chunk)
                  
                  // 发送音频数据到主线程
                  this.port.postMessage({
                    type: 'audiodata',
                    data: audioData,
                    sampleRate: sampleRate,
                    length: audioData.length
                  })
                }
              }
            }
            return true
          }
        }
        
        registerProcessor('asr-processor', ASRProcessor)
      `

      const blob = new Blob([workletCode], { type: 'application/javascript' })
      const workletUrl = URL.createObjectURL(blob)

      await this.audioContext.audioWorklet.addModule(workletUrl)
      URL.revokeObjectURL(workletUrl)

      // 创建worklet节点
      this.workletNode = new AudioWorkletNode(this.audioContext, 'asr-processor')

      // 设置worklet消息处理
      this.workletNode.port.onmessage = (event) => {
        if (event.data.type === 'audiodata') {
          this.handleWorkletData(event.data.data, event.data.sampleRate)
        }
      }

      // 创建音频源并连接
      this.source = this.audioContext.createMediaStreamSource(this.stream)
      this.source.connect(this.workletNode)
      this.workletNode.connect(this.audioContext.destination)

      this.isRecording = true
      console.log('[AudioWorklet] 录音启动成功')

      return this
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        throw { code: 'PERMISSION_DENIED' }
      }
      console.error('[AudioWorklet] 启动失败:', error)
      throw { code: 'AUDIO_ERROR', message: error.message }
    }
  }

  // 处理worklet发送的音频数据
  async handleWorkletData(float32Data, sourceSampleRate) {
    if (!this.isRecording || !this.onDataAvailable) return

    console.log(`[AudioWorklet] 接收音频数据: ${float32Data.length} samples, 采样率: ${sourceSampleRate}Hz`)

    try {
      // worklet已经处理好了200ms的数据块，直接处理
      const processedData = await this.processAudioChunk(float32Data, sourceSampleRate)
      if (this.onDataAvailable) {
        this.onDataAvailable(processedData, sourceSampleRate)
      }
    } catch (error) {
      console.error('[AudioWorklet] 音频处理失败:', error)
    }
  }

  stopRecording() {
    this.isRecording = false

    if (this.workletNode) {
      this.workletNode.disconnect()
      this.workletNode = null
    }

    if (this.source) {
      this.source.disconnect()
      this.source = null
    }

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close()
      this.audioContext = null
    }

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }

    this.audioBuffer = []
    console.log('[AudioWorklet] 录音已停止')
  }

  cleanup() {
    super.cleanup()
    this.stopRecording()
    this.audioBuffer = []
  }
}

// ScriptProcessor音频处理器 - 降级兼容方案（支持旧版浏览器）
class ScriptAudioProcessor extends BaseAudioProcessor {
  constructor() {
    super()
    this.audioContext = null
    this.stream = null
    this.source = null
    this.processor = null
    this.audioBuffer = []
    this.bufferTargetSize = Math.round(this.targetSampleRate * this.segmentDurationMs / 1000) // 200ms = 3200 samples at 16kHz
  }

  isSupported() {
    return BrowserCapability.checkMediaDevices() && BrowserCapability.checkAudioContext()
  }

  async startRecording() {
    if (!this.isSupported()) {
      throw { code: 'NOT_SUPPORTED' }
    }

    try {
      // Firefox兼容性处理：构建灵活的音频约束
      const audioConstraints = {
        channelCount: this.targetChannels,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: false
      }

      // Chrome可以指定采样率，Firefox让其自动选择
      if (BrowserCapability.isChrome()) {
        audioConstraints.sampleRate = this.targetSampleRate
      }

      const constraints = {
        audio: audioConstraints
      }

      // 获取麦克风流
      this.stream = await navigator.mediaDevices.getUserMedia(constraints)

      // 创建AudioContext
      const AudioContextClass = window.AudioContext || window.webkitAudioContext

      // Firefox兼容性处理：不强制指定采样率，让其使用系统默认
      const audioContextOptions = {}

      // 只有在Chrome中才尝试指定采样率
      if (BrowserCapability.isChrome()) {
        audioContextOptions.sampleRate = this.targetSampleRate
      }

      try {
        this.audioContext = new AudioContextClass(audioContextOptions)
        console.log(`[ScriptProcessor] 创建AudioContext，采样率: ${this.audioContext.sampleRate}Hz`)
      } catch (error) {
        // 如果指定采样率失败，使用默认设置
        console.warn(`[ScriptProcessor] 指定采样率失败，使用默认设置:`, error)
        this.audioContext = new AudioContextClass()
        console.log(`[ScriptProcessor] 降级AudioContext，采样率: ${this.audioContext.sampleRate}Hz`)
      }

      // 创建音频源
      this.source = this.audioContext.createMediaStreamSource(this.stream)

      // 创建ScriptProcessor节点
      const actualSampleRate = this.audioContext.sampleRate
      let bufferSize = Math.round(actualSampleRate * 0.1) // 100ms缓冲区，更频繁触发

      // ScriptProcessor要求bufferSize必须是2的幂次方
      const validBufferSizes = [256, 512, 1024, 2048, 4096, 8192, 16384]
      bufferSize = validBufferSizes.find(size => size >= bufferSize) || 4096

      console.log(`[ScriptProcessor] 使用缓冲区大小: ${bufferSize} samples (约${Math.round(bufferSize / actualSampleRate * 1000)}ms)`)

      this.processor = this.audioContext.createScriptProcessor(bufferSize, 1, 1)

      // 设置音频处理回调
      this.processor.onaudioprocess = (event) => {
        if (this.isRecording) {
          const inputBuffer = event.inputBuffer
          const channelData = inputBuffer.getChannelData(0)

          // 处理音频数据
          this.handleScriptProcessorData(new Float32Array(channelData), actualSampleRate)
        }
      }

      // 连接音频节点
      this.source.connect(this.processor)
      this.processor.connect(this.audioContext.destination)

      this.isRecording = true
      console.log('[ScriptProcessor] 录音启动成功')

      return this
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        throw { code: 'PERMISSION_DENIED' }
      }
      console.error('[ScriptProcessor] 启动失败:', error)
      throw { code: 'AUDIO_ERROR', message: error.message }
    }
  }

  // 处理ScriptProcessor发送的音频数据
  async handleScriptProcessorData(float32Data, sourceSampleRate) {
    if (!this.isRecording || !this.onDataAvailable) return

    // 累积音频数据到缓冲区
    this.audioBuffer.push(...float32Data)

    // 当缓冲区达到200ms时，处理并发送数据
    while (this.audioBuffer.length >= this.bufferTargetSize) {
      const chunk = new Float32Array(this.audioBuffer.splice(0, this.bufferTargetSize))

      try {
        const processedData = await this.processAudioChunk(chunk, sourceSampleRate)
        if (this.onDataAvailable) {
          this.onDataAvailable(processedData, sourceSampleRate)
        }
      } catch (error) {
        console.error('[ScriptProcessor] 音频处理失败:', error)
      }
    }
  }

  stopRecording() {
    this.isRecording = false

    if (this.processor) {
      this.processor.disconnect()
      this.processor = null
    }

    if (this.source) {
      this.source.disconnect()
      this.source = null
    }

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close()
      this.audioContext = null
    }

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }

    this.audioBuffer = []
    console.log('[ScriptProcessor] 录音已停止')
  }

  cleanup() {
    super.cleanup()
    this.stopRecording()
    this.audioBuffer = []
  }
}

// 音频处理器工厂 - 智能选择最佳处理器
class AudioProcessorFactory {
  static async createBestProcessor(options = {}) {
    const attempts = []

    // 获取浏览器信息
    const isFirefox = BrowserCapability.isFirefox()
    const isChrome = BrowserCapability.isChrome()

    console.log(`[ProcessorFactory] 检测浏览器: Firefox=${isFirefox}, Chrome=${isChrome}`)

    // 定义尝试顺序（根据浏览器和兼容性优化）
    const processorTypes = []

    // Firefox: 优先AudioWorklet
    if (isFirefox) {
      processorTypes.push(
        { type: ProcessorType.AUDIO_WORKLET, class: AudioWorkletProcessor, name: 'AudioWorklet' },
        { type: ProcessorType.SCRIPT_PROCESSOR, class: ScriptAudioProcessor, name: 'ScriptProcessor' }
      )
    }
    // Chrome: 也优先AudioWorklet，但保持兼容性
    else if (isChrome) {
      processorTypes.push(
        { type: ProcessorType.AUDIO_WORKLET, class: AudioWorkletProcessor, name: 'AudioWorklet' },
        { type: ProcessorType.SCRIPT_PROCESSOR, class: ScriptAudioProcessor, name: 'ScriptProcessor' }
      )
    }
    // 其他浏览器: 先尝试现代API，再降级
    else {
      processorTypes.push(
        { type: ProcessorType.AUDIO_WORKLET, class: AudioWorkletProcessor, name: 'AudioWorklet' },
        { type: ProcessorType.SCRIPT_PROCESSOR, class: ScriptAudioProcessor, name: 'ScriptProcessor' }
      )
    }

    // 逐个尝试处理器
    for (const processorInfo of processorTypes) {
      try {
        console.log(`[ProcessorFactory] 尝试创建 ${processorInfo.name} 处理器...`)

        const processor = new processorInfo.class()

        // 检查支持性
        if (processor.isSupported()) {
          console.log(`[ProcessorFactory] ✅ ${processorInfo.name} 处理器创建成功`)
          return {
            processor,
            type: processorInfo.type,
            name: processorInfo.name
          }
        } else {
          console.log(`[ProcessorFactory] ❌ ${processorInfo.name} 处理器不支持`)
          attempts.push(`${processorInfo.name}: 不支持`)
        }
      } catch (error) {
        console.error(`[ProcessorFactory] ❌ ${processorInfo.name} 处理器创建失败:`, error)
        attempts.push(`${processorInfo.name}: ${error.message}`)
      }
    }

    // 所有处理器都失败
    const errorMessage = `所有音频处理器都不可用。尝试记录: ${attempts.join('; ')}`
    console.error(`[ProcessorFactory] ${errorMessage}`)
    throw {
      code: 'NOT_SUPPORTED',
      message: errorMessage,
      attempts
    }
  }

  // 获取处理器能力信息
  static getProcessorCapabilities() {
    return {
      [ProcessorType.AUDIO_WORKLET]: {
        name: 'AudioWorklet',
        supported: BrowserCapability.checkAudioWorklet(),
        performance: 'high',
        latency: 'low',
        browsers: ['Firefox 76+', 'Chrome 66+', 'Safari 14.1+'],
        features: ['不阻塞主线程', '低延迟', '高性能']
      },
      [ProcessorType.SCRIPT_PROCESSOR]: {
        name: 'ScriptProcessor',
        supported: BrowserCapability.checkAudioContext(),
        performance: 'medium',
        latency: 'medium',
        browsers: ['所有现代浏览器'],
        features: ['广泛兼容', '已废弃但可用'],
        deprecated: true
      }
    }
  }

  // 获取推荐处理器
  static getRecommendedProcessor() {
    const bestType = BrowserCapability.getBestProcessorType()
    const capabilities = this.getProcessorCapabilities()

    return {
      type: bestType,
      info: capabilities[bestType] || null,
      fallbacks: Object.keys(capabilities).filter(type =>
        type !== bestType && capabilities[type].supported
      )
    }
  }
}

// API通信器 - 使用统一API管理
class ASRApiClient {
  constructor() {
    // 不再需要baseUrl和timeout，由request统一管理
  }

  // 开始ASR会话
  async startSession(sessionId, audioConfig = {}, tenantId = null, userId = null, source = null) {
    try {
      const config = {
        sample_rate: 16000,
        channels: 1,
        bits_per_sample: 16,
        ...audioConfig
      }

      const response = await asrStart({
        session_id: sessionId,
        user_id: userId,
        audio_config: config,
        tenant_id: tenantId,
        source: source
      })

      // request已经处理了Result格式，直接返回data
      return response.data
    } catch (error) {
      console.error('[ASR API] 启动会话失败:', error)
      throw {
        code: 'SESSION_ERROR',
        message: `启动会话失败: ${error.message}`
      }
    }
  }

  // 发送音频数据
  async sendAudioData(sessionId, sequence, audioData, isLast = false) {
    try {
      const requestData = {
        session_id: sessionId,
        sequence: sequence,
        audio_data: audioData, // Base64编码的GZIP压缩PCM数据
        is_last: isLast
      }

      console.log(`[ASR API] 发送音频数据:`, {
        session_id: sessionId,
        sequence: sequence,
        audio_data_length: audioData.length,
        audio_data_preview: audioData.substring(0, 30) + '...',
        is_last: isLast
      })

      const response = await asrAudio(requestData)

      // request已经处理了Result格式，直接返回data
      console.log(`[ASR API] 音频发送成功，响应:`, response.data)
      return response.data
    } catch (error) {
      console.error('[ASR API] 发送音频失败:', error)
      throw {
        code: 'NETWORK_ERROR',
        message: `发送音频失败: ${error.message}`
      }
    }
  }

  // 结束ASR会话
  async finishSession(sessionId) {
    try {
      const response = await asrFinish({
        session_id: sessionId
      })

      // request已经处理了Result格式，直接返回data
      return response.data
    } catch (error) {
      console.error('[ASR API] 结束会话失败:', error)
      throw {
        code: 'API_ERROR',
        message: `结束会话失败: ${error.message}`
      }
    }
  }
}

// 文本合并器 - 处理重叠识别结果
class TextMerger {
  constructor() {
    this.previousResults = []
    this.accumulatedText = ''
    this.lastProcessedSequence = 0
    this.baseText = '' // 基础文本（原有输入内容）
    this.finalizedText = '' // 已确定的最终文本部分
  }

  reset() {
    this.previousResults = []
    this.accumulatedText = ''
    this.lastProcessedSequence = 0
    this.baseText = ''
    this.finalizedText = ''
  }

  // 合并新的识别结果，处理重叠内容
  mergeResult(result) {
    if (!result || !result.text) {
      return this.accumulatedText
    }

    const newText = result.text.trim()
    if (!newText) {
      return this.accumulatedText
    }

    // 如果是最终结果，将其添加到已确定文本中
    if (result.is_final) {
      // 将新的最终结果合并到已确定的文本中
      this.finalizedText = this.smartMerge(this.finalizedText, newText)
      this.accumulatedText = this.finalizedText

      this.previousResults.push({
        text: newText,
        sequence: result.sequence,
        is_final: true,
        timestamp: Date.now()
      })
      return this.accumulatedText
    }

    // 处理临时结果：临时结果只是预览，不修改已确定的文本
    const tempText = this.smartMerge(this.finalizedText, newText)
    this.accumulatedText = tempText

    // 保存临时结果（覆盖之前的临时结果）
    this.previousResults = this.previousResults.filter(r => r.is_final)
    this.previousResults.push({
      text: newText,
      sequence: result.sequence,
      is_final: false,
      timestamp: Date.now()
    })

    return this.accumulatedText
  }

  // 智能文本合并算法
  smartMerge(baseText, newText) {
    if (!baseText) return newText
    if (!newText) return baseText

    // 去除首尾空格
    const base = baseText.trim()
    const incoming = newText.trim()

    // 如果新文本完全包含在基础文本中，返回基础文本
    if (base.includes(incoming)) {
      return base
    }

    // 如果新文本完全包含基础文本，返回新文本
    if (incoming.includes(base)) {
      return incoming
    }

    // 查找重叠部分
    const overlap = this.findOverlap(base, incoming)

    if (overlap.length > 0) {
      // 有重叠，去重合并
      const baseWithoutOverlap = base.substring(0, base.lastIndexOf(overlap))
      return (baseWithoutOverlap + incoming).trim()
    } else {
      // 无重叠，直接拼接（添加空格分隔）
      return (base + ' ' + incoming).trim()
    }
  }

  // 查找两个文本之间的重叠部分
  findOverlap(text1, text2) {
    let maxOverlap = ''

    // 从text1的末尾开始，寻找与text2开头的最长匹配
    for (let i = 1; i <= Math.min(text1.length, text2.length); i++) {
      const suffix = text1.substring(text1.length - i)
      const prefix = text2.substring(0, i)

      if (suffix === prefix && suffix.length > maxOverlap.length) {
        maxOverlap = suffix
      }
    }

    // 特殊处理：按词进行匹配
    const words1 = text1.split(/\s+/)
    const words2 = text2.split(/\s+/)

    for (let i = 1; i <= Math.min(words1.length, words2.length); i++) {
      const suffixWords = words1.slice(-i).join(' ')
      const prefixWords = words2.slice(0, i).join(' ')

      if (suffixWords === prefixWords && suffixWords.length > maxOverlap.length) {
        maxOverlap = suffixWords
      }
    }

    return maxOverlap
  }

  // 获取当前累积的文本
  getAccumulatedText() {
    return this.accumulatedText
  }

  // 设置基础文本（用于添加到现有内容后面）
  setBaseText(baseText) {
    this.baseText = baseText || ''
    this.finalizedText = this.baseText
    this.accumulatedText = this.baseText
  }
}

// 识别状态管理器
class RecognitionState {
  constructor() {
    this.reset()
  }

  reset() {
    this.isRecording = false
    this.isProcessing = false
    this.sessionId = null
    this.sequence = 0
    this.currentText = ''
    this.finalText = ''
    this.error = null
    this.results = []
  }

  updateState(updates) {
    Object.assign(this, updates)
  }

  addResult(result) {
    this.results.push({
      ...result,
      timestamp: Date.now()
    })

    if (result.is_final) {
      this.finalText = result.text
    } else {
      this.currentText = result.text
    }
  }
}

// 语音识别管理器 - 主控制器
class VoiceRecognitionManager {
  constructor(options = {}) {
    this.maxRecordingTime = options.maxRecordingTime || 60000 // 默认60秒

    // 初始化各个模块
    this.audioProcessor = null // 动态创建最佳处理器
    this.processorInfo = null // 处理器信息
    this.apiClient = new ASRApiClient()
    this.state = new RecognitionState()
    this.textMerger = new TextMerger()

    // 定时器相关
    this.recordingTimer = null

    // 回调函数
    this.onError = null
    this.onStateChange = null
    this.onTextUpdate = null // 新增：文本更新回调
    this.onTimeLimit = null // 新增：时间限制回调
  }

  // 检查浏览器支持（异步检查最佳处理器）
  async isSupported() {
    try {
      if (!this.audioProcessor) {
        await this._initializeProcessor()
      }
      return !!this.audioProcessor
    } catch (error) {
      console.error('[语音识别] 支持性检查失败:', error)
      return false
    }
  }

  // 初始化音频处理器
  async _initializeProcessor() {
    if (this.audioProcessor) {
      return // 已经初始化
    }

    try {
      console.log('[语音识别] 初始化音频处理器...')
      const result = await AudioProcessorFactory.createBestProcessor()

      this.audioProcessor = result.processor
      this.processorInfo = result

      console.log(`[语音识别] ✅ 使用 ${result.name} 处理器`)

      // 设置处理器回调
      this.audioProcessor.onDataAvailable = async(processedData, sourceSampleRate) => {
        await this.handleAudioData(processedData, sourceSampleRate)
      }
    } catch (error) {
      console.error('[语音识别] 初始化音频处理器失败:', error)
      throw error
    }
  }

  // 生成会话ID
  generateSessionId() {
    return 'asr_session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
  }

  // 开始语音识别
  async startRecognition(originalText = '') {
    try {
      // 重置状态
      this.state.reset()
      this.textMerger.reset()
      this.textMerger.setBaseText(originalText) // 设置原始文本作为基础

      // 清除之前的定时器
      this._clearRecordingTimer()

      this.state.updateState({ isProcessing: true })
      this._notifyStateChange()

      // 1. 确保音频处理器已初始化
      await this._initializeProcessor()

      // 2. 生成会话ID
      this.state.sessionId = this.generateSessionId()

      // 3. 启动API会话
      const tenantId = store.getters.tenantId
      const userId = store.getters.userId
      const sessionResult = await this.apiClient.startSession(this.state.sessionId, {}, tenantId, userId, Source.CARGO_WARE)

      if (!sessionResult.success) {
        throw { code: 'SESSION_ERROR', message: '服务器拒绝创建会话' }
      }

      // 4. 开始录音（处理器回调已在初始化时设置）
      await this.audioProcessor.startRecording()

      // 5. 设置定时器，60秒后自动停止录音
      this._startRecordingTimer()

      // 6. 更新状态
      this.state.updateState({
        isRecording: true,
        isProcessing: false
      })

      this._notifyStateChange()

      // 立即发送包含原有文本的更新通知，确保输入框显示原有内容
      this._notifyTextUpdate(this.textMerger.getAccumulatedText(), false)

      console.log(`[语音识别] 开始录音成功, 会话ID: ${this.state.sessionId}, 处理器: ${this.processorInfo.name}, 最大录制时间: ${this.maxRecordingTime / 1000}秒`)
    } catch (error) {
      this._clearRecordingTimer()
      this.state.updateState({
        isRecording: false,
        isProcessing: false,
        error: ErrorHandler.handle(error)
      })

      this._notifyError(error)
      throw error
    }
  }

  // 处理音频数据（处理器已经处理了音频，这里只需要发送API和处理结果）
  async handleAudioData(processedAudio, sourceSampleRate = 44100) {
    try {
      // 1. 增加序列号
      this.state.sequence++

      // 2. 发送到服务器
      const result = await this.apiClient.sendAudioData(
        this.state.sessionId,
        this.state.sequence,
        processedAudio,
        false
      )

      // 3. 处理识别结果
      if (result.success && result.results && result.results.length > 0) {
        result.results.forEach(res => {
          // 使用文本合并器处理重叠内容
          const mergedText = this.textMerger.mergeResult({
            ...res,
            sequence: this.state.sequence
          })

          // 更新状态
          this.state.addResult(res)

          // 通知文本更新
          this._notifyTextUpdate(mergedText, res.is_final)
        })
      }
    } catch (error) {
      console.error('[语音识别] 处理音频数据失败:', error)
      this._notifyError(error)
    }
  }

  // 停止语音识别
  async stopRecognition() {
    try {
      // 清除定时器
      this._clearRecordingTimer()

      this.state.updateState({ isProcessing: true })
      this._notifyStateChange()

      // 1. 停止录音
      if (this.audioProcessor) {
        this.audioProcessor.stopRecording()
      }

      let finalMergedText = ''

      // 2. 结束API会话（finish接口会自动清理服务器资源）
      if (this.state.sessionId) {
        try {
          const result = await this.apiClient.finishSession(this.state.sessionId)
          console.log('[语音识别] 会话结束，服务器资源已自动清理:', this.state.sessionId)
        } catch (apiError) {
          console.error('[语音识别] API结束会话失败:', apiError)
        }
      }

      // 3. 确认当前累积的文本为最终结果
      finalMergedText = this.textMerger.getAccumulatedText()

      // 将当前累积的文本标记为已确定文本，避免后续处理时的重复
      if (finalMergedText && finalMergedText !== this.textMerger.finalizedText) {
        this.textMerger.finalizedText = finalMergedText
      }

      // 4. 更新状态
      this.state.updateState({
        isRecording: false,
        isProcessing: false,
        finalText: finalMergedText
      })

      this._notifyStateChange()

      console.log(`[语音识别] 停止录音成功，处理器: ${this.processorInfo?.name || 'Unknown'}，最终文本:`, finalMergedText)
      return finalMergedText
    } catch (error) {
      this._clearRecordingTimer()
      this.state.updateState({
        isRecording: false,
        isProcessing: false,
        error: ErrorHandler.handle(error)
      })

      this._notifyError(error)
      throw error
    }
  }

  // 清理资源
  cleanup() {
    try {
      // 1. 清除定时器
      this._clearRecordingTimer()

      // 2. 如果还在录音，先停止录音
      if (this.state.isRecording && this.audioProcessor) {
        console.log('[语音识别] 清理时检测到正在录音，先停止录音')
        this.audioProcessor.stopRecording()
      }

      // 3. 清理各模块资源（服务器资源由finish接口自动清理）
      if (this.audioProcessor) {
        this.audioProcessor.cleanup()
        this.audioProcessor = null
      }
      this.processorInfo = null
      this.textMerger.reset()
      this.state.reset()

      console.log('[语音识别] 客户端资源清理完成')
    } catch (error) {
      console.error('[语音识别] 资源清理过程中出错:', error)
    }
  }

  // 通知回调函数
  _notifyError(error) {
    if (this.onError) {
      this.onError(ErrorHandler.handle(error))
    }
  }

  _notifyStateChange() {
    if (this.onStateChange) {
      this.onStateChange({
        isRecording: this.state.isRecording,
        isProcessing: this.state.isProcessing,
        currentText: this.state.currentText,
        finalText: this.state.finalText,
        error: this.state.error,
        sessionId: this.state.sessionId
      })
    }
  }

  // 新增：通知文本更新
  _notifyTextUpdate(mergedText, isFinal) {
    if (this.onTextUpdate) {
      this.onTextUpdate(mergedText, isFinal)
    }
  }

  // 启动录音定时器
  _startRecordingTimer() {
    this.recordingTimer = setTimeout(async() => {
      console.log(`[语音识别] 录音时间达到${this.maxRecordingTime / 1000}秒限制，自动停止录音`)

      try {
        // 通知时间限制回调
        if (this.onTimeLimit) {
          this.onTimeLimit()
        }

        // 自动停止录音
        await this.stopRecognition()
      } catch (error) {
        console.error('[语音识别] 自动停止录音失败:', error)
        this._notifyError(error)
      }
    }, this.maxRecordingTime)

    console.log(`[语音识别] 录音定时器已启动，${this.maxRecordingTime / 1000}秒后自动停止`)
  }

  // 清除录音定时器
  _clearRecordingTimer() {
    if (this.recordingTimer) {
      clearTimeout(this.recordingTimer)
      this.recordingTimer = null
      console.log('[语音识别] 录音定时器已清除')
    }
  }
}

// 导出ASR工具类
export const asrUtils = {
  // 主要类
  VoiceRecognitionManager,

  // 音频处理器类
  BaseAudioProcessor,
  AudioWorkletProcessor,
  ScriptAudioProcessor,
  AudioProcessorFactory,

  // 其他核心类
  ASRApiClient,
  RecognitionState,
  TextMerger,

  // 工具类
  BrowserCapability,
  ErrorHandler,
  ProcessorType,
  Source,

  // 创建管理器实例
  createManager(options = {}) {
    return new VoiceRecognitionManager(options)
  },

  // 检查浏览器兼容性（增强版本）
  checkCompatibility() {
    return {
      mediaDevices: BrowserCapability.checkMediaDevices(),
      mediaRecorder: BrowserCapability.checkMediaRecorder(),
      audioContext: BrowserCapability.checkAudioContext(),
      audioWorklet: BrowserCapability.checkAudioWorklet(),
      supportedMimeTypes: BrowserCapability.getSupportedMimeTypes(),
      bestProcessor: BrowserCapability.getBestProcessorType(),
      browser: {
        isChrome: BrowserCapability.isChrome(),
        isFirefox: BrowserCapability.isFirefox()
      },
      processorCapabilities: AudioProcessorFactory.getProcessorCapabilities(),
      recommended: AudioProcessorFactory.getRecommendedProcessor()
    }
  },

  // 获取音频处理器信息
  getProcessorInfo() {
    return AudioProcessorFactory.getProcessorCapabilities()
  }
}

export default asrUtils
